import { ChecklistFieldItem } from '$tablet-app/src/app/checklists/components/ChecklistFieldItem';
import Card from '$tablet-app/src/app/components/Card';
import {
  FloatingActions,
  useFloatingActionControl,
} from '$tablet-app/src/app/components/FloatingActions';
import { InfoDialogButton } from '$tablet-app/src/app/components/InfoDialogButton';
import { KeyboardAvoid } from '$tablet-app/src/app/components/KeyboardAvoid';
import LoadingSpinner from '$tablet-app/src/app/components/LoadingSpinner';
import { Panel } from '$tablet-app/src/app/components/Panel';
import Row from '$tablet-app/src/app/components/Row';
import { ScreenBackButton } from '$tablet-app/src/app/components/ScreenBackButton';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import { ScreenTitle } from '$tablet-app/src/app/components/ScreenTitle';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import TextInputWithButtons from '$tablet-app/src/app/components/TextInputWithButtons';
import ContactTagButton from '$tablet-app/src/app/contacts/components/ContactTagButton';
import { palette } from '$tablet-app/src/app/theme';
import { filterExcludeByID } from '$tablet-app/src/app/utils/records';
import { Button } from '@akin-home/buttons/native';
import { ContactTypes, contact_tags } from '@akin-home/contacts';
import { LargeInput } from '@akin-home/forms/native';
import { colors, spacing } from '@akin-home/theme';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { Switch } from 'react-native-switch';
import styled from 'styled-components/native';
import { v4 as uuid } from 'uuid';
import DeleteButton from '../../components/DeleteButton';
import { RobotIdentity } from '../types';

interface Props {
  screenType: 'edit' | 'create';

  identity: Partial<RobotIdentity>;
  onChange?: (identity: Partial<RobotIdentity>) => void;
  onCancel?: () => void;

  isSubmitting?: boolean;
  onSubmit?: () => void;

  onDelete?: () => void;

  isEditingFaceEmbeddings?: boolean;
  onChangeFaceEmbeddings?: () => void;
  onDeleteFaceEmbeddings?: () => void;
}

export const RobotIdentityFormScreenView = (props: Props) => {
  const { screenType, identity, onCancel, onSubmit, onChange, onDelete } =
    props;

  const { scrollHandler, floating_action_styles } = useFloatingActionControl();

  const onChangeName = (name: string) => onChange?.({ ...identity, name });

  const onChangePrimaryUser = (newVal: boolean) =>
    onChange?.({ ...identity, is_primary_user: newVal });

  const onChangeNote = (note: string) =>
    onChange?.({
      ...identity,
      notes: note,
    });

  const onToggleTag = (label: ContactTypes) =>
    onChange?.({
      ...identity,
      roles: identity.roles?.includes(label)
        ? identity.roles.filter((t) => t !== label)
        : [...(identity.roles ?? []), label],
    });

  // useRecordThumbnail({ record: identity });

  const onChangeItem = (item: { text: string; id: string }) => {
    onChange?.({
      ...identity,
      nicknames: identity?.nicknames?.map((i) => (i.id === item.id ? item : i)),
    });
  };

  const onRemoveItem = (id: string) => {
    onChange?.({
      ...identity,
      nicknames: identity?.nicknames?.filter(filterExcludeByID(id)),
    });
  };

  const onSubmitNewNicknameItem = (nickname: string) => {
    onChange?.({
      ...identity,
      nicknames: [
        ...(identity?.nicknames || []),
        { id: uuid(), text: nickname },
      ],
    });
  };

  return (
    <Screen>
      <Header>
        <ScreenBackButton onPress={onCancel} />

        <ScreenTitle>
          {screenType === 'edit'
            ? 'Edit Robot Identity'
            : 'Create Robot Identity'}
        </ScreenTitle>
      </Header>

      <KeyboardAvoid>
        <Scroll contentContainerStyle={styles.scroll} onScroll={scrollHandler}>
          {/* Name */}
          <TopCard>
            <ImagePlaceholder>
              <FontAwesomeIcon size={50} color="lightgrey" icon="user" />
            </ImagePlaceholder>

            <TopDetailsRight>
              <View>
                <LargeInput
                  value={identity.name}
                  placeholder="Name"
                  onChangeText={onChangeName}
                  textContentType="name"
                />
              </View>

              <SwitchField>
                <View>
                  <Switch
                    value={identity.is_primary_user}
                    switchLeftPx={3}
                    switchRightPx={3}
                    barHeight={32}
                    circleSize={25}
                    onValueChange={onChangePrimaryUser}
                    renderActiveText={false}
                    renderInActiveText={false}
                    backgroundActive={colors.primary[70]}
                    circleBorderWidth={0}
                    renderInsideCircle={() => (
                      <FontAwesomeIcon
                        icon={'star'}
                        color={identity.is_primary_user ? 'gold' : 'lightgrey'}
                      />
                    )}
                  />
                </View>

                <Text variant="h3" weight="semibold">
                  Primary User
                </Text>

                <InfoDialogButton
                  title="Primary User"
                  body={
                    <Text weight="semibold" style={styles.infoDialogHeader}>
                      Is the main user who will be supported by the Robot.
                    </Text>
                  }
                />
              </SwitchField>
            </TopDetailsRight>
          </TopCard>

          {/* Nicknames */}
          <DetailPanel>
            <DetailLeft>
              <Text variant="h3" weight="semibold">
                Nicknames
              </Text>
            </DetailLeft>

            <Spacer height="30px" width="1px;" color="lightgrey" />

            <FieldRow>
              {identity?.nicknames?.length &&
              identity?.nicknames?.length > 0 ? (
                <ScrollView
                  horizontal
                  contentContainerStyle={{}}
                  style={styles.nicknamesScrollView}
                >
                  {identity?.nicknames?.map((nickname) => (
                    <NicknameContainer>
                      <ChecklistFieldItem
                        key={nickname.id}
                        id={nickname.id.toString()}
                        text={nickname.text}
                        onChange={onChangeItem}
                        onRemove={onRemoveItem}
                      />
                    </NicknameContainer>
                  ))}
                </ScrollView>
              ) : null}

              <AddNicknameContainer>
                <TextInputWithButtons
                  blurOnSubmit={false}
                  placeholder={'Add nickname'}
                  onSubmit={onSubmitNewNicknameItem}
                />
              </AddNicknameContainer>
            </FieldRow>
          </DetailPanel>

          {/* Face ID */}
          <DetailPanel>
            <DetailLeft style={styles.faceIdContainer}>
              <Text variant="h3" weight="semibold">
                Face ID Enabled?
              </Text>

              <InfoDialogButton
                title="Face ID"
                body={
                  <Text weight="semibold" style={styles.infoDialogHeader}>
                    {`Face ID allows the robot to recognise this person. Click ${
                      identity.has_face_embeddings ? 'Relearn' : 'Learn'
                    } Face on the right to enable.`}
                  </Text>
                }
              />
            </DetailLeft>
            <Spacer height="100%" width="1px;" color="lightgrey" />
            <View style={styles.faceIdInnerContainer}>
              <Text variant="h3">
                {identity.has_face_embeddings ? '✅ Yes' : '❌ No'}
              </Text>

              <View style={styles.faceIdButtonsContainer}>
                {identity.has_face_embeddings ? (
                  <DeleteButton
                    buttonText="Delete Face"
                    onConfirm={() => {
                      props?.onDeleteFaceEmbeddings?.();
                    }}
                  />
                ) : null}

                <Button
                  icon={'edit'}
                  variant="secondary"
                  onPress={() => props?.onChangeFaceEmbeddings?.()}
                >
                  {identity.has_face_embeddings ? 'Relearn Face' : 'Learn Face'}
                </Button>
              </View>

              {props.isEditingFaceEmbeddings ? (
                <View
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    opacity: 0.8,
                  }}
                >
                  <LoadingSpinner size="large" />
                </View>
              ) : null}
            </View>
          </DetailPanel>

          {/* Roles */}
          <DetailPanel>
            <DetailLeft>
              <Text variant="h3" weight="semibold">
                Roles
              </Text>
            </DetailLeft>
            <Spacer height="100%" width="1px;" color="lightgrey" />
            <ScrollView horizontal>
              <Row gap={10}>
                {contact_tags.map((tag) => (
                  <ContactTagButton
                    key={tag.label}
                    label={tag.label}
                    emoji={tag.emoji}
                    selected={identity.roles?.includes(tag.label)}
                    onPress={(label) => onToggleTag(label as ContactTypes)}
                  />
                ))}
              </Row>
            </ScrollView>
          </DetailPanel>

          <DetailPanel>
            <DetailLeft>
              <Text variant="h3" weight="semibold">
                Notes
              </Text>
            </DetailLeft>

            <Spacer height="30px" width="1px;" color="lightgrey" />

            <DetailInput
              value={identity.notes}
              onChangeText={onChangeNote}
              placeholder="Type notes here"
              multiline
            />
          </DetailPanel>
        </Scroll>
      </KeyboardAvoid>

      <FloatingActions style={floating_action_styles}>
        {onDelete ? (
          <DeleteButton
            onConfirm={onDelete}
            deleteModalProps={{
              title: 'Are you sure you want to delete this identity?',
            }}
          />
        ) : null}

        <Button
          onPress={onSubmit}
          disabled={props.isSubmitting ?? false}
          style={props.isSubmitting ? { backgroundColor: palette.dim } : {}}
          icon={props.isSubmitting ? 'loader' : 'check'}
          size="large"
          iconSize={20}
        >
          {props.isSubmitting ? 'Saving' : 'Save'}
        </Button>
      </FloatingActions>
    </Screen>
  );
};

const DetailInput = styled.TextInput`
  flex: 1;
  font-size: 22px;
  font-weight: 600;
`;

const Screen = styled.View`
  flex: 1;
`;

const TopCard = styled(Card)`
  flex-direction: row;
  padding: 16px;
  gap: 34px;
`;

const ImagePlaceholder = styled.View`
  height: 131px;
  width: 131px;

  border-radius: 50px;

  justify-content: center;
  align-items: center;

  background-color: #f5f7f9;
`;

const TopDetailsRight = styled.View`
  flex: 1;
  gap: 20px;
`;

const Header = styled(ScreenHeader)`
  gap: 20px;
  justify-content: flex-start;
  align-items: center;
`;

const DetailPanel = styled(Panel)`
  padding: 18px 24px;
  flex-direction: row;
  gap: 35px;
  align-items: center;
`;

const DetailLeft = styled.View`
  width: 160px;
`;

const SwitchField = styled.View`
  flex-direction: row;
  gap: 10px;
  align-items: center;
`;

const Scroll = styled(Animated.ScrollView)`
  flex: 1;
`;

const FieldRow = styled.View`
  flex-direction: row;
  flex: 1;
  gap: 16px;
`;

const NicknameContainer = styled.View`
  flex: 1;
  width: 300%;
  margin-right: 15px;
`;

const AddNicknameContainer = styled.View`
  flex: 1;
`;

const styles = StyleSheet.create({
  infoDialogHeader: {
    textAlign: 'center',
  },
  scroll: {
    gap: 16,
    paddingHorizontal: 20,
    paddingBottom: 100,
  },
  faceIdContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  faceIdInnerContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  faceIdButtonsContainer: {
    marginLeft: 'auto',
    flexDirection: 'row',
    gap: spacing[2],
    paddingRight: spacing[1],
  },
  nicknamesScrollView: {
    flex: 1,
  },
});
